import React, { useState, useEffect } from 'react';
import { Save, AlertCircle, CheckCircle, Zap, Mail, Bot, Search, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import { Alert, AlertDescription } from '../../ui/alert';
import { supabase } from '../../../lib/supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const DEFAULTS = {
  email_candidate_confirmations: false,
  email_candidate_status_updates: false,
  auto_generate_match_profile: false,
  auto_send_outreach: false,
  auto_send_min_match_pct: 75,
  highlight_min_match_pct: 60,
  linkedin_email_fallback_enabled: false,
  linkedin_search_prompt: '',
};

/**
 * Every automated behavior added across the portal roadmap gets its own
 * toggle here, defaulting to off. Nothing on this page runs by itself until
 * switched on deliberately - that's a firm requirement, not a convenience.
 */
const ToggleRow = ({ icon: Icon, title, description, checked, onChange, children }) => (
  <div className="flex items-start justify-between gap-4 py-4 border-b last:border-b-0">
    <div className="flex items-start gap-3 min-w-0">
      {Icon && <Icon className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" />}
      <div className="min-w-0">
        <h4 className="font-medium text-sm">{title}</h4>
        <p className="text-sm text-muted-foreground">{description}</p>
        {checked && children && <div className="mt-3">{children}</div>}
      </div>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} />
  </div>
);

const AutomationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState(DEFAULTS);
  const [refining, setRefining] = useState(false);
  const [refineError, setRefineError] = useState(null);
  const [suggestion, setSuggestion] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error: settingsError } = await supabase
        .from('recruiter_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') {
        throw settingsError;
      }

      if (data) {
        setFormData({ ...DEFAULTS, ...data });
      } else {
        const { data: newSettings, error: createError } = await supabase
          .from('recruiter_settings')
          .insert({ user_id: user.id })
          .select()
          .single();

        if (createError) throw createError;
        setFormData({ ...DEFAULTS, ...newSettings });
      }
    } catch (err) {
      console.error('Error fetching automation settings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      if (formData.auto_send_min_match_pct < formData.highlight_min_match_pct) {
        throw new Error('Auto-send threshold should be at or above the highlight threshold.');
      }

      const { error: updateError } = await supabase
        .from('recruiter_settings')
        .update({
          email_candidate_confirmations: formData.email_candidate_confirmations,
          email_candidate_status_updates: formData.email_candidate_status_updates,
          auto_generate_match_profile: formData.auto_generate_match_profile,
          auto_send_outreach: formData.auto_send_outreach,
          auto_send_min_match_pct: formData.auto_send_min_match_pct,
          highlight_min_match_pct: formData.highlight_min_match_pct,
          linkedin_email_fallback_enabled: formData.linkedin_email_fallback_enabled,
          linkedin_search_prompt: formData.linkedin_search_prompt,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      setSuccess('Automation settings saved');
      await fetchSettings();
    } catch (err) {
      console.error('Error saving automation settings:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const set = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSuggestRefinement = async () => {
    try {
      setRefining(true);
      setRefineError(null);
      setSuggestion(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${API_URL}/linkedin-scout/suggest-prompt-refinement`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` },
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Could not generate a suggestion');
      }

      setSuggestion(result);
    } catch (err) {
      console.error('Error suggesting prompt refinement:', err);
      setRefineError(err.message);
    } finally {
      setRefining(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Automation</h2>
        <p className="text-muted-foreground">
          Every automated feature here defaults to off. Turn one on when you're ready to test it -
          nothing runs by itself until you do.
        </p>
      </div>

      {error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Mail className="h-5 w-5 mr-2 text-indigo-600" />
            Candidate Emails
          </CardTitle>
          <CardDescription>Transactional emails sent to candidates about their own applications</CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <ToggleRow
            title="Application confirmation"
            description="Email a candidate confirming their application was received (guests included)."
            checked={formData.email_candidate_confirmations}
            onChange={(v) => set('email_candidate_confirmations', v)}
          />
          <ToggleRow
            title="Unsuccessful notification"
            description="Email a candidate when their application status becomes rejected or unsuitable."
            checked={formData.email_candidate_status_updates}
            onChange={(v) => set('email_candidate_status_updates', v)}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Bot className="h-5 w-5 mr-2 text-indigo-600" />
            Matching &amp; Outreach
          </CardTitle>
          <CardDescription>Automation around RFQ matching and candidate outreach</CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <ToggleRow
            title="Auto-generate match profile"
            description="When an RFQ becomes invited, automatically run the free skill-match scan (no AI cost)."
            checked={formData.auto_generate_match_profile}
            onChange={(v) => set('auto_generate_match_profile', v)}
          />
          <ToggleRow
            title="Auto-send outreach"
            description="Automatically draft/send outreach emails to candidates above the match threshold below, for visible invited RFQs."
            checked={formData.auto_send_outreach}
            onChange={(v) => set('auto_send_outreach', v)}
          >
            <div className="grid grid-cols-2 gap-4 max-w-md">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">
                  Auto-send threshold (%)
                </label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={formData.auto_send_min_match_pct}
                  onChange={(e) => set('auto_send_min_match_pct', parseInt(e.target.value, 10) || 0)}
                  className="w-full border rounded-md px-3 py-1.5 text-sm bg-background"
                />
              </div>
            </div>
          </ToggleRow>
          <div className="pt-4">
            <label className="text-xs font-medium text-muted-foreground block mb-1">
              Highlight threshold (%) - always active, no toggle
            </label>
            <p className="text-xs text-muted-foreground mb-2">
              Candidates scoring at or above this are visually highlighted on the matches page with a
              one-click email action, regardless of auto-send.
            </p>
            <input
              type="number"
              min={0}
              max={100}
              value={formData.highlight_min_match_pct}
              onChange={(e) => set('highlight_min_match_pct', parseInt(e.target.value, 10) || 0)}
              className="w-full max-w-[200px] border rounded-md px-3 py-1.5 text-sm bg-background"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Zap className="h-5 w-5 mr-2 text-indigo-600" />
            LinkedIn
          </CardTitle>
          <CardDescription>
            Posting and outreach automation is deliberately limited - see LinkedIn Templates for what's
            available. There is no "auto-post" or "auto-connect" toggle here on purpose.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <ToggleRow
            title="Email me the post when live posting isn't available"
            description="If an RFQ is flagged to post to LinkedIn but there's no working API connection, email you the ready-to-paste post text instead."
            checked={formData.linkedin_email_fallback_enabled}
            onChange={(v) => set('linkedin_email_fallback_enabled', v)}
          />

          <div className="pt-4 border-t mt-4">
            <div className="flex items-center gap-2 mb-1">
              <Search className="h-4 w-4 text-muted-foreground" />
              <h4 className="font-medium text-sm">LinkedIn scouting search brief</h4>
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              Plain-language description of what makes a good candidate, used by the read-only
              scouting agent (see an RFQ's matches page → "Search LinkedIn for candidates"). Nothing
              here posts or contacts anyone - it only shapes what the agent looks for.
            </p>
            <textarea
              value={formData.linkedin_search_prompt}
              onChange={(e) => set('linkedin_search_prompt', e.target.value)}
              rows={6}
              className="w-full border rounded-md px-3 py-2 text-sm bg-background font-mono"
            />
            <div className="mt-2 flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={handleSuggestRefinement} disabled={refining}>
                <Sparkles className={`h-3.5 w-3.5 mr-1.5 ${refining ? 'animate-pulse' : ''}`} />
                {refining ? 'Thinking...' : 'Suggest a refinement from my feedback'}
              </Button>
            </div>

            {refineError && (
              <Alert className="bg-red-50 border-red-200 mt-2">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800 text-sm">{refineError}</AlertDescription>
              </Alert>
            )}

            {suggestion && (
              <div className="mt-3 border rounded-md p-3 bg-muted/30 space-y-2">
                <p className="text-xs text-muted-foreground">
                  Based on {suggestion.sampleSize} of your good/bad-marked leads. Review before using -
                  nothing is applied automatically.
                </p>
                <p className="text-sm whitespace-pre-wrap font-mono">{suggestion.suggestedPrompt}</p>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => { set('linkedin_search_prompt', suggestion.suggestedPrompt); setSuggestion(null); }}
                >
                  Use this text
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end">
        <Button onClick={handleSave} disabled={saving}>
          <Save className={`h-4 w-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

export default AutomationSettings;
